import type React from 'react'
import { useState, useEffect } from 'react'
import { useMagoMaster } from '../hooks/useMagoMaster'
import { useDestMaster } from '../hooks/useDestMaster'
import { useSubDestMaster } from '../hooks/useSubDestMaster'
import { validateMagoMaster, hasErrors } from '../utils/validation'
import { DEVICE_STATUS_MAP } from '../types/master'
import type { MagoMasterRequest, MagoMasterResponse, FormErrors } from '../types/master'

interface FormFieldProps {
  label: string
  name: string
  value: string | number | undefined
  type?: 'text' | 'select'
  required?: boolean
  placeholder?: string
  options?: Array<{ value: string | number; label: string }>
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  onBlur: (field: string) => void
  error?: string
  touched?: boolean
}

function FormField({ label, name, value, type = 'text', required, placeholder, options, onChange, onBlur, error, touched }: FormFieldProps) {
  const fieldClass = `form-control ${touched && error ? 'is-invalid' : ''}`
  const selectClass = `form-select ${touched && error ? 'is-invalid' : ''}`
  
  return (
    <div className="mb-3">
      <label htmlFor={name} className="form-label">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      {type === 'select' ? (
        <select
          className={selectClass}
          id={name}
          name={name}
          value={value ?? ''}
          onChange={onChange}
          onBlur={() => onBlur(name)}
          required={required}
        >
          {options?.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          className={fieldClass}
          id={name}
          name={name}
          value={value ?? ''}
          onChange={onChange}
          onBlur={() => onBlur(name)}
          placeholder={placeholder}
          required={required}
        />
      )}
      {touched && error && (
        <div className="invalid-feedback">{error}</div>
      )}
    </div>
  )
}

interface MagoMasterFormProps {
  device?: MagoMasterResponse | null
  onClose: () => void
  onSuccess: () => void
}

export default function MagoMasterForm({ device, onClose, onSuccess }: MagoMasterFormProps) {
  const { createDevice, updateDevice, loading } = useMagoMaster()
  const { destinations } = useDestMaster()
  const { subDestinations } = useSubDestMaster()
  const [formData, setFormData] = useState<MagoMasterRequest>({
    dest_id: 0,
    sub_dest_id: null,
    lot_id: '',
    group_id: '',
    product_id: '',
    mago_id: '',
    sim_id: '',
    lte_type: '',
    lte_plan: '',
    lte_apn: '',
    status: 0,
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (device) {
      console.log('Form receiving device data:', device)
      setFormData({
        dest_id: device.dest_id,
        sub_dest_id: device.sub_dest_id,
        lot_id: device.lot_id || '',
        group_id: device.group_id || '',
        product_id: device.product_id || '',
        mago_id: device.mago_id,
        sim_id: device.sim_id || '',
        lte_type: device.lte_type || '',
        lte_plan: device.lte_plan || '',
        lte_apn: device.lte_apn || '',
        status: device.status,
      })
      // Clear any previous errors when receiving new device data
      setErrors({})
      setTouched({})
    }
  }, [device])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    let processedValue: string | number | null = value

    if (name === 'dest_id' || name === 'status') {
      processedValue = value === '' ? 0 : Number.parseInt(value, 10)
    } else if (name === 'sub_dest_id') {
      processedValue = value === '' || value === '0' ? null : Number.parseInt(value, 10)
    } else if (value === '') {
      processedValue = null
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }))
    }
  }

  const handleBlur = (field: string) => {
    setTouched(prev => ({
      ...prev,
      [field]: true,
    }))

    // Validate single field
    const fieldErrors = validateMagoMaster(formData)
    setErrors(prev => ({
      ...prev,
      [field]: fieldErrors[field] || '',
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate all fields
    const validationErrors = validateMagoMaster(formData)
    setErrors(validationErrors)

    if (hasErrors(validationErrors)) {
      return
    }

    try {
      if (device) {
        await updateDevice(device.id, formData)
      } else {
        await createDevice(formData)
      }
      // Wait a bit for database to propagate changes
      setTimeout(() => {
        onSuccess()
      }, 100)
    } catch (error) {
      console.error('Failed to save device:', error)
    }
  }

  const isEditing = !!device

  const destinationOptions = [
    { value: 0, label: 'Select destination...' },
    ...destinations.map(dest => ({ value: dest.id, label: dest.name }))
  ]

  const subDestinationOptions = [
    { value: '', label: 'Select sub destination (optional)...' },
    ...subDestinations.map(subDest => ({ value: subDest.id, label: subDest.name }))
  ]

  const statusOptions = Object.entries(DEVICE_STATUS_MAP).map(([key, value]) => ({
    value: Number.parseInt(key, 10) || 0,
    label: value?.label || key
  }))

  return (
    <div
      className="modal show d-block"
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-microchip me-2" />
              {isEditing ? 'Edit Device' : 'Add New Device'}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={loading}
            />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-6">
                  <FormField
                    label="Destination"
                    name="dest_id"
                    value={formData.dest_id}
                    type="select"
                    required
                    options={destinationOptions}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={errors.dest_id}
                    touched={touched.dest_id}
                  />
                </div>
                <div className="col-md-6">
                  <FormField
                    label="Device ID"
                    name="mago_id"
                    value={formData.mago_id}
                    required
                    placeholder="MAGO001"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={errors.mago_id}
                    touched={touched.mago_id}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <FormField
                    label="Sub Destination"
                    name="sub_dest_id"
                    value={formData.sub_dest_id || ''}
                    type="select"
                    options={subDestinationOptions}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={errors.sub_dest_id}
                    touched={touched.sub_dest_id}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <FormField
                    label="Lot ID"
                    name="lot_id"
                    value={formData.lot_id || ''}
                    placeholder="LOT001"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={errors.lot_id}
                    touched={touched.lot_id}
                  />
                </div>
                <div className="col-md-4">
                  <FormField
                    label="Group ID"
                    name="group_id"
                    value={formData.group_id || ''}
                    placeholder="GROUP01"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={errors.group_id}
                    touched={touched.group_id}
                  />
                </div>
                <div className="col-md-4">
                  <FormField
                    label="Product ID"
                    name="product_id"
                    value={formData.product_id || ''}
                    placeholder="MSPK-V1"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={errors.product_id}
                    touched={touched.product_id}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <FormField
                    label="SIM ID"
                    name="sim_id"
                    value={formData.sim_id || ''}
                    placeholder="SIM001"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={errors.sim_id}
                    touched={touched.sim_id}
                  />
                </div>
                <div className="col-md-6">
                  <FormField
                    label="Status"
                    name="status"
                    value={formData.status}
                    type="select"
                    options={statusOptions}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={errors.status}
                    touched={touched.status}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <FormField
                    label="LTE Type"
                    name="lte_type"
                    value={formData.lte_type || ''}
                    placeholder="4G, 5G"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={errors.lte_type}
                    touched={touched.lte_type}
                  />
                </div>
                <div className="col-md-4">
                  <FormField
                    label="LTE Plan"
                    name="lte_plan"
                    value={formData.lte_plan || ''}
                    placeholder="standard, premium"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={errors.lte_plan}
                    touched={touched.lte_plan}
                  />
                </div>
                <div className="col-md-4">
                  <FormField
                    label="APN"
                    name="lte_apn"
                    value={formData.lte_apn || ''}
                    placeholder="internet"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    error={errors.lte_apn}
                    touched={touched.lte_apn}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                キャンセル
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    {isEditing ? '更新中...' : '作成中...'}
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2" />
                    {isEditing ? '更新' : '作成'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
